import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import GestureIcon from '@mui/icons-material/Gesture';

import { OperationService } from '../../../services';
import type { OperationResult } from '../../../domain';
import {
  DEFAULT_SKETCH_TO_IMAGE_STATE,
  buildSketchToImageRequest,
  type SketchToImageFormState,
} from './buildRequests';
import { useOperationMutation } from './useOperationMutation';
import { submitErrorMessage } from '../../../components/auth/signInRequiredError';
import { mergePromptWithSwatches } from '../../shared/panels/swatchSelection';

interface Props {
  ensureSession: () => Promise<string>;
  isCreatingSession?: boolean;
  /** The canvas's sketch dataURL (dark strokes on white). Null when blank. */
  sketchDataUrl: string | null;
  swatchPrompt?: string;
  onResult?: (result: OperationResult) => void;
  onRequestClearSketch?: () => void;
}

export const SketchToImagePanel: React.FC<Props> = ({
  ensureSession,
  isCreatingSession = false,
  sketchDataUrl,
  swatchPrompt,
  onResult,
  onRequestClearSketch,
}) => {
  const [state, setState] = useState<SketchToImageFormState>(DEFAULT_SKETCH_TO_IMAGE_STATE);
  const [validationError, setValidationError] = useState<string | null>(null);

  // SketchToImage opts out of bake-on-submit: its `sourceImageBase64` is
  // the sketch tablet, not a filter-eligible canvas image. The user's
  // CSS-filter sliders apply to the loaded image layer, which sketch mode
  // hides — nothing to bake.
  const mutation = useOperationMutation(OperationService.sketchToImage, {
    canvasSourceField: null,
  });

  const hasSketch = !!sketchDataUrl;
  const promptOk = state.prompt.trim().length > 0;

  const handleSubmit = async () => {
    try {
      const sid = await ensureSession();
      const mergedPrompt = mergePromptWithSwatches(state.prompt, swatchPrompt ?? '');
      const request = buildSketchToImageRequest(
        { ...state, prompt: mergedPrompt },
        sid,
        sketchDataUrl,
      );
      setValidationError(null);
      const result = await mutation.mutateAsync(request);
      onResult?.(result);
    } catch (err) {
      setValidationError(submitErrorMessage(err));
    }
  };

  const submitDisabled =
    mutation.isPending || isCreatingSession || !hasSketch || !promptOk;

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} alignItems="center">
        <GestureIcon fontSize="small" />
        <Typography variant="subtitle2">Sketch to image</Typography>
      </Stack>

      {!hasSketch && (
        <Alert severity="info">Sketch on the canvas to feed the model.</Alert>
      )}

      <TextField
        label="Prompt"
        value={state.prompt}
        onChange={e => setState(s => ({ ...s, prompt: e.target.value }))}
        placeholder="Describe the finished image"
        multiline
        minRows={2}
        maxRows={5}
        size="small"
        fullWidth
        required
      />

      <Box>
        <Typography variant="caption" color="text.secondary">
          Sketch strength · {state.sketchStrength.toFixed(2)}
        </Typography>
        <Slider
          value={state.sketchStrength}
          onChange={(_, v) =>
            setState(s => ({ ...s, sketchStrength: Array.isArray(v) ? v[0] : v }))
          }
          min={0}
          max={1}
          step={0.05}
          size="small"
        />
      </Box>

      <Stack direction="row" spacing={1}>
        <TextField
          label="Width (px)"
          type="number"
          value={state.width}
          onChange={e => setState(s => ({ ...s, width: Number(e.target.value) }))}
          size="small"
          inputProps={{ min: 256, max: 4096, step: 64 }}
        />
        <TextField
          label="Height (px)"
          type="number"
          value={state.height}
          onChange={e => setState(s => ({ ...s, height: Number(e.target.value) }))}
          size="small"
          inputProps={{ min: 256, max: 4096, step: 64 }}
        />
      </Stack>

      {validationError && <Alert severity="warning">{validationError}</Alert>}
      {mutation.isError && (
        <Alert severity="error">
          {mutation.error instanceof Error ? mutation.error.message : 'Sketch render failed.'}
        </Alert>
      )}

      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitDisabled}
          startIcon={
            mutation.isPending || isCreatingSession ? (
              <CircularProgress size={16} />
            ) : (
              <GestureIcon />
            )
          }
        >
          {isCreatingSession ? 'Starting session…' : mutation.isPending ? 'Rendering…' : 'Render'}
        </Button>
        {onRequestClearSketch && (
          <Button
            variant="outlined"
            size="small"
            onClick={onRequestClearSketch}
            disabled={!hasSketch || mutation.isPending}
          >
            Clear sketch
          </Button>
        )}
      </Stack>
    </Stack>
  );
};
